import React, { lazy } from 'react';
import { BedDoubleIcon, Clock3Icon, MapPinIcon, UtensilsIcon } from 'lucide-react';
import { motion } from 'framer-motion';
type Day = {
  day: string;
  title: string;
  altitude: string;
  image: string;
  alt: string;
  description: string[];
  distance: string;
  time: string;
  meals: string;
  stay: string;
};
const days: Day[] = [{
  day: '01',
  title: 'Arrive in Kathmandu',
  altitude: '1,300m',
  image: 'https://images.unsplash.com/photo-1605649487212-47bdab064df7?auto=format&fit=crop&w=1400&q=85',
  alt: 'Kathmandu city beneath a blue sky',
  description: ['Meet your trek leader at Tribhuvan International Airport and transfer to a calm, centrally located hotel. After checking in, there is time to rest, exchange currency or take a short first walk through the lively streets of Thamel.', 'Later, gather for a welcome lunch, detailed route briefing and equipment check. Your guide will review permits, altitude awareness, trekking etiquette and the road plan before a relaxed Nepali dinner introduces the group to the journey ahead.'],
  distance: 'Airport transfer',
  time: '30–45 min',
  meals: 'Breakfast, Lunch & Dinner',
  stay: '4-star hotel'
}, {
  day: '02',
  title: 'Kathmandu to Machha Khola drive',
  altitude: '930m',
  image: 'https://images.unsplash.com/photo-1501179691627-eeaa65ea017c?auto=format&fit=crop&w=1400&q=85',
  alt: 'Mountain road beside a river in Nepal',
  description: ['Leave Kathmandu early and follow the Prithvi Highway through foothill towns, rice terraces and roadside markets. After turning north, the road becomes narrower and more dramatic as it follows the Budi Gandaki corridor toward the Manaslu region.', 'The final section is a true Himalayan approach: slow, scenic and full of changing valley views. Arrive in Machha Khola in the late afternoon, settle into the local guest house and enjoy a hearty meal beside the river before the trek begins.'],
  distance: '150 km',
  time: '4 hours drive',
  meals: 'Breakfast, Lunch & Dinner',
  stay: 'Local guest house'
}, {
  day: '03',
  title: 'Trek from Machha Khola to Jagat',
  altitude: '1,340m',
  image: 'https://images.unsplash.com/photo-1548013146-72479768bada?auto=format&fit=crop&w=1400&q=85',
  alt: 'A suspension bridge in a green Himalayan valley',
  description: ['The first walking day follows the Budi Gandaki through warm forest, cultivated terraces and small riverside settlements. Cross suspension bridges, pause at waterfalls and pass Tatopani, where natural hot springs have long offered rest to travelers.', 'As the valley tightens, the trail rises over stone stairways and lands in Jagat, a handsome slate-roofed village. This is a gentle introduction to the rhythm of trekking: walk steadily, drink often and let the mountains set the pace.'],
  distance: '14 km',
  time: '5 hours trek',
  meals: 'Breakfast, Lunch & Dinner',
  stay: 'Local guest house'
}, {
  day: '04',
  title: 'Trek from Jagat to Deng',
  altitude: '1,860m',
  image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1400&q=85',
  alt: 'Forest trail through the Himalayan foothills',
  description: ['After the Manaslu Conservation Area checkpoint, the route climbs through sal forest toward Philim, a traditional Gurung village set above the river. Prayer stones, mani walls and changing architecture begin to signal the cultural transition ahead.', 'Continue beyond Philim on a quieter trail where the gorge becomes narrow and cool. The path passes bamboo, pine and tumbling streams before reaching Deng, a small settlement where the evening feels noticeably more remote than the night before.'],
  distance: '20 km',
  time: '6–7 hours trek',
  meals: 'Breakfast, Lunch & Dinner',
  stay: 'Local guest house'
}, {
  day: '05',
  title: 'Trek from Deng to Namrung',
  altitude: '2,630m',
  image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1400&q=85',
  alt: 'Jagged Himalayan ridge above a valley',
  description: ['Today brings a longer climb through bamboo and rhododendron forest, with bridges connecting one side of the gorge to the other. Waterfalls and deep side valleys break the journey, while the air turns cooler as the trail gains altitude.', 'By afternoon, Tibetan influence becomes more visible in the carvings, prayer flags and village design. Namrung sits in a beautiful mountain setting and offers some of the first wide views toward the snow peaks of the Manaslu range.'],
  distance: '19 km',
  time: '6 hours trek',
  meals: 'Breakfast, Lunch & Dinner',
  stay: 'Mountain lodge'
}, {
  day: '06',
  title: 'Trek from Namrung to Samagaun',
  altitude: '3,530m',
  image: 'https://images.unsplash.com/photo-1530789253388-582c481c54b0?auto=format&fit=crop&w=1400&q=85',
  alt: 'Snowy mountain and alpine village',
  description: ['Walk through the villages of Lihi and Lho, where barley fields, carved gateways and monasteries sit beneath enormous Himalayan walls. Near Lho, take time to appreciate one of the route’s classic views: Manaslu rising beyond the village rooftops.', 'The trail opens into the high Nubri valley as it approaches Samagaun. Yaks graze in open pastures, stone homes replace forest, and the vastness of the upper valley makes clear that the trek has entered its alpine chapter.'],
  distance: '18 km',
  time: '6–7 hours trek',
  meals: 'Breakfast, Lunch & Dinner',
  stay: 'Local guest house'
}, {
  day: '07',
  title: 'Acclimatization day in Samagaun',
  altitude: '3,530m',
  image: 'https://images.unsplash.com/photo-1464278533981-50106e6176b1?auto=format&fit=crop&w=1400&q=85',
  alt: 'High alpine trail beneath snow mountains',
  description: ['A slower day here is essential for acclimatization and gives the group time to experience Samagaun beyond the trail. Depending on conditions, take a guided walk toward Birendra Tal, a turquoise glacial lake, or ascend toward Pungyen Gompa for higher views.', 'The principle is simple: climb gently, return to sleep lower and keep the afternoon easy. Back in the village, enjoy tea, visit local shops or simply rest while your guide checks how everyone is adapting to the altitude.'],
  distance: '5–8 km',
  time: '3–4 hours walk',
  meals: 'Breakfast, Lunch & Dinner',
  stay: 'Local guest house'
}, {
  day: '08',
  title: 'Trek from Samagaun to Samdo',
  altitude: '3,875m',
  image: 'https://images.unsplash.com/photo-1486911278844-a81c5267e227?auto=format&fit=crop&w=1400&q=85',
  alt: 'Prayer flags in a Himalayan mountain valley',
  description: ['Follow a broad, open trail along the Budi Gandaki with Manaslu and its surrounding glaciers visible behind you. The walking is shorter today by design, giving the body time to settle before the pass while the terrain feels increasingly high and wild.', 'Samdo is a traditional Tibetan refugee settlement close to the borderlands. Its windswept setting, stone homes and sweeping views create one of the most atmospheric evenings of the trek, especially when the late light reaches the surrounding ridges.'],
  distance: '8 km',
  time: '3–4 hours trek',
  meals: 'Breakfast, Lunch & Dinner',
  stay: 'Mountain lodge'
}, {
  day: '09',
  title: 'Cross Larkya La to Bimthang',
  altitude: '5,106m',
  image: 'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=1400&q=85',
  alt: 'Dramatic snowy Himalayan mountain pass',
  description: ['Begin before dawn for the trek’s defining day. A measured ascent crosses moraine and high snow terrain toward Larkya La, with a guide-led pace designed to keep everyone warm, hydrated and moving steadily in the thin air.', 'At the pass, the panorama reaches from Himlung Himal and Cheo Himal toward Annapurna II. After photographs and a brief celebration, descend carefully over glacial terrain to Bimthang, where forest returns and the day’s effort is rewarded by an unforgettable mountain basin.'],
  distance: '24 km',
  time: '8–9 hours trek',
  meals: 'Breakfast, Lunch & Dinner',
  stay: 'Mountain lodge'
}, {
  day: '10',
  title: 'Trek to Dharapani and drive onward',
  altitude: '1,860m',
  image: 'https://images.unsplash.com/photo-1472396961693-142e6e269027?auto=format&fit=crop&w=1400&q=85',
  alt: 'Green valley and river below the mountains',
  description: ['Descend through rhododendron forest and alongside the Dudh Khola, leaving the high valley behind. The trail softens through pasture and woodland before meeting the Annapurna route near Dharapani, a welcome return to greener, lower country.', 'From Dharapani, continue by vehicle toward Besi Sahar and Kathmandu. The road journey offers time to look back on the terrain just crossed, share stories with the group and return to the city carrying a much quieter sense of the Himalaya.'],
  distance: '18 km + drive',
  time: '5 hours trek + drive',
  meals: 'Breakfast, Lunch & Dinner',
  stay: 'Hotel in Kathmandu'
}];
const outlineDays = ['Day 1 – Arrive Kathmandu · 1,300m', 'Day 2 – Kathmandu to Machha Khola drive · 150 km · 4 hours', 'Day 3 – Machha Khola to Jagat trek · 5 hours', 'Day 4 – Jagat to Deng trek · 6–7 hours', 'Day 5 – Deng to Namrung trek · 6 hours', 'Day 6 – Namrung to Samagaun trek · 6–7 hours', 'Day 7 – Acclimatization walk in Samagaun', 'Day 8 – Samagaun to Samdo trek · 3–4 hours', 'Day 9 – Cross Larkya La to Bimthang · 5,106m', 'Day 10 – Bimthang to Dharapani trek and drive onward'];
export function Itinerary() {
  const trekFacts = [['Duration', '10 days'], ['Max elevation', 'Larkya La · 5,106m'], ['Trek grade', 'Challenging'], ['Group size', '2–12 walkers'], ['Start / finish', 'Kathmandu, Nepal']];
  return <section id="itinerary" className="bg-[#172a27] py-20 text-[#f4f0e7] sm:py-28" data-id="element-213" data-mp="7b3pe43l7g62">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10" data-id="element-214" data-mp="2c0hmbw82vue">
        <header className="mx-auto max-w-3xl text-center" data-id="element-215" data-mp="2csfxuh7f7od">
          <p className="eyebrow text-[#D9CAA0]" data-id="element-216" data-mp="3ar0ptlby59r7">The route, day by day</p>
          <h2 className="mt-4 font-display text-4xl leading-tight sm:text-5xl" data-id="element-217" data-mp="35sphjf1av7b3">
            Ten days from Kathmandu’s hum to the high silence of Larkya.
          </h2>
          <p className="mt-5 text-base leading-8 text-stone-300" data-id="element-218" data-mp="3jttjtwcijzkq">
            Every departure is guided in small groups and shaped around safe
            acclimatization. Times are estimates—mountain travel rewards
            flexibility.
          </p>
        </header>

        <div className="mx-auto mt-14 grid max-w-6xl overflow-hidden border border-white/15 bg-[#203632] lg:grid-cols-2" data-id="element-219" data-mp="uzmobt0qyglh">
          <div className="p-7 sm:p-9" data-id="element-220" data-mp="12ivuzehvc16u">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9CAA0]" data-id="element-221" data-mp="17j0elde2pc1a">
              Outline itinerary
            </p>
            <h3 className="mt-3 font-display text-3xl" data-id="element-222" data-mp="sn2jlyfvydqn">The Manaslu traverse</h3>
            <ol className="mt-7 space-y-3 border-l border-[#D9CAA0]/50 pl-5 text-sm leading-6 text-stone-300" data-id="element-223" data-mp="xkatzcusldhc">
              {outlineDays.map(day => <li key={day} data-id="element-224" data-mp="1lcrmyig2ipvf">{day}</li>)}
            </ol>
            <dl className="mt-8 divide-y divide-white/10 border-t border-white/10 text-sm" data-id="element-225" data-mp="1q98c9fz07aa3">
              {trekFacts.map(([term, detail]) => <div key={term} className="flex justify-between gap-4 py-3" data-id="element-226" data-mp="1gjyizwsfblke">
                  <dt className="text-stone-400" data-id="element-227" data-mp="1ghvj8l1gye2h">{term}</dt>
                  <dd className="text-right font-medium" data-id="element-228" data-mp="248hqia3nfubf">{detail}</dd>
                </div>)}
            </dl>
          </div>

          <div className="relative min-h-[420px] overflow-hidden bg-[#d7d2c2] p-7 text-[#1d2d2a] sm:p-9" data-id="element-229" data-mp="292c9fbbfk59g">
            <div className="absolute inset-0 opacity-25" style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1200&q=60')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} data-id="element-230" data-mp="2itvlg9wmsvpn" />
            <div className="relative h-full" data-id="element-231" data-mp="1zyyh8y5ojehr">
              <p className="text-xs font-bold uppercase tracking-[0.18em]" data-id="element-232" data-mp="1v6nelnjk1eli">
                Route map
              </p>
              <p className="mt-2 max-w-xs text-sm leading-6 text-[#40504a]" data-id="element-233" data-mp="1q72evawk927q">
                An illustrated line from Kathmandu to the high pass at Larkya
                La.
              </p>
              <svg viewBox="0 0 280 210" className="absolute bottom-0 left-0 h-72 w-full" aria-label="Illustrated route from Kathmandu to Larkya La" role="img" data-id="element-234" data-mp="1h7ffsvrb08hu">
                <path d="M35 180 C80 150 44 125 100 104 S92 62 150 65 S170 22 242 30" fill="none" stroke="#6D5A39" strokeWidth="4" strokeDasharray="3 7" data-id="element-235" data-mp="3nums41nlizqx" />
                {[[35, 180, 'KTM'], [82, 143, 'Machha'], [103, 104, 'Jagat'], [151, 65, 'Sama'], [242, 30, 'Larkya']].map(([x, y, label]) => <g key={String(label)} data-id="element-236" data-mp="3a1nek57djfm0">
                    <circle cx={Number(x)} cy={Number(y)} r="6" fill="#172a27" stroke="#D9CAA0" strokeWidth="3" data-id="element-237" data-mp="3e4tgx1rd8mn7" />
                    <text x={Number(x) + 9} y={Number(y) - 9} fill="#172a27" fontSize="11" fontWeight="700" data-id="element-238" data-mp="2mccll9w9mpif">
                      {label}
                    </text>
                  </g>)}
              </svg>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-20 max-w-4xl" data-id="element-239" data-mp="2h2s1nrr58fx0">
          <header className="border-b border-white/20 pb-7 text-center" data-id="element-240" data-mp="2c978qe4eqc4o">
            <p className="eyebrow text-[#D9CAA0]" data-id="element-241" data-mp="2q13zadqfgdyy">Detailed itinerary</p>
            <h3 className="mt-3 font-display text-3xl sm:text-4xl" data-id="element-242" data-mp="2q3gggood2kay">
              Day to day on the trail
            </h3>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-stone-300" data-id="element-243" data-mp="2zzjiluj7qixw">
              Each day is designed around realistic mountain pacing, time to
              absorb the villages and landscapes, and the flexibility required
              in a remote Himalayan region.
            </p>
          </header>

          <div className="mt-8 space-y-10" data-id="element-244" data-mp="10ngwecj7fagj">
            {days.map((item, index) => <motion.article key={item.day} initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.12
          }} transition={{
            duration: 0.4,
            delay: Math.min(index * 0.03, 0.2)
          }} className="overflow-hidden border border-white/10 bg-[#203632]" data-id="element-245" data-mp="3c9iwkysbmggl" data-mpi={index}>
                <img src={item.image} alt={item.alt} className="h-72 w-full object-cover sm:h-96" loading="lazy" data-id="element-246" data-mp="2jm6lbkrm1v2l" />
                <div className="p-6 sm:p-8" data-id="element-247" data-mp="hv1tanlrmshq">
                  <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-248" data-mp="hrxfcyii0idv">
                    <p className="text-xs font-bold tracking-[0.2em] text-[#D9CAA0]" data-id="element-249" data-mp="msbpcm3nu2ej">
                      DAY {item.day}
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-250" data-mp="gcbjpizp5d17">{item.altitude}</p>
                  </div>
                  <h4 className="mt-2 font-display text-3xl" data-id="element-251" data-mp="bd8f1znmp8tr">{item.title}</h4>
                  <div className="mt-5 space-y-4 text-sm leading-7 text-stone-300 sm:text-base" data-id="element-252" data-mp="6eospy54hv0v">
                    {item.description.map(paragraph => <p key={paragraph.slice(0, 35)} data-id="element-253" data-mp="141w66bv26sf8">{paragraph}</p>)}
                  </div>
                  <div className="mt-7 grid gap-3 border-t border-white/10 pt-5 text-xs text-stone-300 sm:grid-cols-2" data-id="element-254" data-mp="z5fhngjy6c1m">
                    <span className="flex items-center gap-2" data-id="element-255" data-mp="pdj7360octgi">
                      <MapPinIcon size={15} className="text-[#D9CAA0]" aria-hidden="true" data-id="element-256" data-mp="pczxqdp3bde3" />
                      {item.distance}
                    </span>
                    <span className="flex items-center gap-2" data-id="element-257" data-mp="1qb4cy5napyip">
                      <Clock3Icon size={15} className="text-[#D9CAA0]" aria-hidden="true" data-id="element-258" data-mp="1qcxjlkxwsiqp" />
                      {item.time}
                    </span>
                    <span className="flex items-center gap-2" data-id="element-259" data-mp="24avv8ikui12i">
                      <UtensilsIcon size={15} className="text-[#D9CAA0]" aria-hidden="true" data-id="element-260" data-mp="2489dxr85k773" />
                      {item.meals}
                    </span>
                    <span className="flex items-center gap-2" data-id="element-261" data-mp="2i320vaqdgpnp">
                      <BedDoubleIcon size={15} className="text-[#D9CAA0]" aria-hidden="true" data-id="element-262" data-mp="2n0u24vcwpo4k" />
                      {item.stay}
                    </span>
                  </div>
                </div>
              </motion.article>)}
          </div>

          <aside className="mt-10 border border-[#D9CAA0]/60 p-6 sm:p-8" aria-label="Important note" data-id="element-263" data-mp="3i8om2c71zyet">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9CAA0]" data-id="element-264" data-mp="3raxt2s3459ng">
              Important note
            </p>
            <p className="mt-4 text-sm leading-7 text-stone-200 sm:text-base sm:leading-8" data-id="element-265" data-mp="3mbl8yf3mmgok">
              The Manaslu Circuit Trek is one of Nepal&apos;s most spectacular
              remote trekking adventures, offering breathtaking Himalayan
              scenery, authentic Tibetan-influenced villages, and an
              unforgettable crossing of the iconic Larke Pass (5,106 m). Since
              the trek passes through a restricted area, all trekkers are
              required to obtain a Manaslu Restricted Area Permit (RAP) and must
              be accompanied by a licensed trekking guide. Independent trekking
              is not permitted.
            </p>
          </aside>
        </div>
      </div>
    </section>;
}